import React, { useState, useRef } from 'react';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { useT } from './LocaleContext';
import { useNavigate } from 'react-router-dom';
import { GlobalStyle, DropdownItem, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import { useOutsideClick, validatePassword, validateUsername, validateEmail, checkHoneypot, getErrorMessage } from './common';
import { authAPI } from './api';
import { 
  RegisterContainer, 
  Title, 
  Form, 
  Input, 
  Button, 
  Message,
  InputWrapper,
  RequiredStar,
  PasswordRequirements,
  UsernameRequirements,
  PasswordInputContainer,
  LoadingSpinner
} from './styles/RegisterPageStyles';


const RegisterPage = () => {
  const t = useT();
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Honeypot поле для защиты от ботов
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => {setMenuOpen((prev) => !prev);};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) {
      return;
    }

    // Проверка honeypot поля (защита от ботов)
    if (!checkHoneypot(honeypot)) {
      // Бот обнаружил - молча отклоняем (не показываем ошибку, чтобы не раскрывать защиту)
      return;
    }

    if (!email || !password || !confirmPassword) {
      setMessage({ text: t('validation.emailAndPasswordRequired'), success: false });
      return;
    }

    // Валидация email
    const emailValidation = validateEmail(email.trim());
    if (!emailValidation.valid) {
      setMessage({ text: emailValidation.message, success: false });
      return;
    }

    // Валидация имени пользователя, если оно указано
    if (userName && userName.trim()) {
      const usernameValidation = validateUsername(userName.trim());
      if (!usernameValidation.valid) {
        setMessage({ text: usernameValidation.message, success: false });
        return;
      }
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setMessage({ text: passwordValidation.message, success: false });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ text: t('validation.passwordsMismatch'), success: false });
      return;
    }

    try {
      setLoading(true);
      const registerData = {
        email: email.trim(),
        password,
        confirm_password: confirmPassword
      };
      
      // Добавляем user_name только если оно указано
      if (userName && userName.trim()) {
        registerData.user_name = userName.trim();
      }
      
      await authAPI.register(registerData);
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate('/activate', { state: { email: email.trim(), fromRegistration: true } });
    } catch (error) {
      setLoading(false);
      const errorMessage = getErrorMessage(error, t('common.unknownError'));
      setMessage({ 
        text: t('register.signUpError', { message: errorMessage }), 
        success: false 
      });
    }
  };

  return (
    <RegisterContainer>
      <GlobalStyle />
      {message && <Message success={message.success}>{message.text}</Message>}
      <MenuButton onClick={toggleMenu} $open={menuOpen}>≡</MenuButton>
      <DropdownMenu ref={dropdownRef} open={menuOpen}>
        <DropdownItem to="/">{t('nav.home')}</DropdownItem>
        <DropdownItem to="/login">{t('nav.login')}</DropdownItem>
        <ThemeToggle />
        <LanguageToggle />
      </DropdownMenu>
      
      <Title>{t('register.title')}</Title>
      <Form onSubmit={handleSubmit}>
        <PasswordInputContainer spacing="8px">
          <UsernameRequirements>
            {t('register.usernameHint')}
          </UsernameRequirements>
          <InputWrapper>
            <Input
              type="text"
              placeholder={t('common.name')}
              value={userName}
              maxLength={50}
              onChange={(e) => {
                // Фильтруем ввод, оставляя только английские буквы
                const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                setUserName(value);
              }}
            />
          </InputWrapper>
        </PasswordInputContainer>
        <InputWrapper>
          <Input
            type="text"
            placeholder={t('common.email')}
            value={email}
            maxLength={254}
            onChange={(e) => setEmail(e.target.value.trim())}
          />
          <RequiredStar>*</RequiredStar>
        </InputWrapper>
        <PasswordInputContainer spacing="8px">
          <PasswordRequirements>
            {t('register.passwordHint')}
          </PasswordRequirements>
          <InputWrapper>
            <Input
              type="password"
              placeholder={t('common.password')}
              value={password}
              maxLength={128}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <RequiredStar>*</RequiredStar>
          </InputWrapper>
        </PasswordInputContainer>
        <InputWrapper>
          <Input
            type="password"
            placeholder={t('common.confirmPassword')}
            value={confirmPassword}
            maxLength={128}
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && email && password && confirmPassword) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
          />
          <RequiredStar>*</RequiredStar>
        </InputWrapper>
        {/* Honeypot поле - скрыто от пользователей, но видно ботам */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            tabIndex: -1
          }}
          autoComplete="off"
          aria-hidden="true"
        />
        <Button as="button" type="submit" disabled={loading}>
          {loading ? <LoadingSpinner /> : t('register.signUp')}
        </Button>
      </Form>
    </RegisterContainer>
  );
};

export default RegisterPage;
